import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CampaignQuestionDto } from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson, apiVoid } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';

// ============================================================================
// Types
// ============================================================================

export interface CreateQuestionData {
  text: string;
  type: string;
  options?: string[];
  displayOrder?: number;
}

export interface UpdateQuestionData {
  questionId: string;
  updates: {
    text?: string;
    type?: string;
    options?: string[];
    displayOrder?: number;
    isActive?: boolean;
  };
}

// ============================================================================
// Query Hooks
// ============================================================================

export function useCampaignQuestions() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.campaignQuestions.list(),
    queryFn: () =>
      apiJson<CampaignQuestionDto[]>(`/api/campaigns/${selectedCampaign!.id}/questions?activeOnly=true`, {
        error: 'Failed to fetch campaign questions',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateQuestion() {
  const queryClient = useQueryClient();
  const { selectedCampaign } = useCampaignStore();

  return useMutation({
    mutationFn: async (data: CreateQuestionData) =>
      apiJson<CampaignQuestionDto>(`/api/campaigns/${selectedCampaign!.id}/questions`, {
        method: 'POST',
        json: data,
        error: (body, response) => {
          const error = new Error(body.message || 'Failed to create question') as Error & { status: number };
          error.status = response.status;
          return error;
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaignQuestions.all });
    },
  });
}

export function useUpdateQuestion() {
  const queryClient = useQueryClient();
  const { selectedCampaign } = useCampaignStore();

  return useMutation({
    mutationFn: async ({ questionId, updates }: UpdateQuestionData) =>
      apiJson<CampaignQuestionDto>(`/api/campaigns/${selectedCampaign!.id}/questions/${questionId}`, {
        method: 'PUT',
        json: updates,
        error: (body, response) => {
          const error = new Error(body.message || 'Failed to update question') as Error & { status: number };
          error.status = response.status;
          return error;
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaignQuestions.all });
    },
  });
}

export function useDeactivateQuestion() {
  const queryClient = useQueryClient();
  const { selectedCampaign } = useCampaignStore();

  return useMutation({
    mutationFn: async (questionId: string) =>
      apiVoid(`/api/campaigns/${selectedCampaign!.id}/questions/${questionId}`, {
        method: 'DELETE',
        error: (body) => new Error(body.message || 'Failed to deactivate question'),
      }),
    onMutate: async (questionId: string) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.campaignQuestions.list() });

      const previous = queryClient.getQueryData<CampaignQuestionDto[]>(
        queryKeys.campaignQuestions.list()
      );

      queryClient.setQueryData<CampaignQuestionDto[]>(
        queryKeys.campaignQuestions.list(),
        (old) => old?.filter((q) => q.id !== questionId)
      );

      return { previous };
    },
    onError: (_err, _questionId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.campaignQuestions.list(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.campaignQuestions.all });
    },
  });
}
